import { defModule } from '@st/redux'
import { Effect } from 'effect'
import { Auth, Routing } from '~/effects'
import type { AuthError } from './auth-client-adapter'
import { routes, type AppRoute } from '~/features/routes'
import type { OAuthProvider } from '@st/sdk'
import { observeLocation } from '~/features/module-runtime'

type OAuthLoginState =
  | { status: 'idle' }
  | { status: 'working'; code: string; state: string }
  | { status: 'failed'; code: string; state: string; error: AuthError }

export type OAuthLoginMessage =
  | { type: 'oauthCallback'; provider: OAuthProvider; code: string; state: string }
  | { type: 'oauthFailed'; code: string; state: string; error: AuthError }

type OAuthLoginInit = undefined

const missingRedirectError: AuthError = {
  type: 'unknown',
  message: 'Missing redirect URL'
}

const observeOAuthCallback = observeLocation(({ pathname }): OAuthLoginMessage | null => {
  const route = routes.match(pathname) ?? null
  if (!route || route.name != 'oauth_callback' || !route.code || !route.state) {
    return null
  }

  const provider = parseOAuthProvider(route.provider)
  if (!provider) {
    return {
      type: 'oauthFailed',
      code: route.code,
      state: route.state,
      error: { type: 'unknown', message: `Unknown OAuth provider: ${route.provider}` }
    }
  }

  return { type: 'oauthCallback', provider, code: route.code, state: route.state }
})

export const oauthLoginModule = defModule<{
  state: OAuthLoginState
  message: OAuthLoginMessage
  init: OAuthLoginInit
}>({
  init: () => ({ status: 'idle' }),
  tasks: () => [observeOAuthCallback()],
  update: (state, message) => {
    switch (message.type) {
      case 'oauthCallback':
        if (
          state.status === 'working' &&
          state.code == message.code &&
          state.state == message.state
        ) {
          return state
        }
        return [
          { status: 'working', code: message.code, state: message.state },
          runOAuthCallback(message.provider, message.code, message.state)
        ]
      case 'oauthFailed':
        return {
          status: 'failed',
          code: message.code,
          state: message.state,
          error: message.error
        }
    }
  }
})

function parseOAuthProvider(value: unknown): OAuthProvider | null {
  switch (value) {
    case 'karbon':
    case 'thinkific':
      return value
    default:
      return null
  }
}

function runOAuthCallback(provider: OAuthProvider, code: string, state: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const routing = yield* Routing

    const result = yield* auth.loginWithOAuthCode(provider, code, state)

    if (result.status == 'succeeded') {
      if (!result.redirectUrl) {
        return { type: 'oauthFailed', code, state, error: missingRedirectError } as const
      }

      yield* routing.redirectTo(result.redirectUrl)
      return null
    }

    return { type: 'oauthFailed', code, state, error: result.error } as const
  })
}
